import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Loader, Modal } from "@hid-galaxy-ui/galaxy-react";
import parsePhoneNumberFromString from "libphonenumber-js";
import sanitizeHtml from "sanitize-html";
import PartnerDetailForm from "../../../../../create-new-partner/partner-detail-form";
import { PartnerFormData } from "../../../../../../../hooks/usePartnerForm";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { updatePartnerSagaAction } from "../../../../../../../redux/saga/partner-details/saga-actions";
import {
  selectPartnerDetails,
  selectUpdatePartnerLoading,
} from "../../../../../../../redux/slices/partner-details/selectors";
import { getValidDateOrNull } from "../../../../../../../utils/getValidDateOrNull";
import { Constants } from "../../../../../../../utils/constants";
import { CountryCode } from "../../../../../../../utils/CountryCode";
import "../../../../../create-new-partner/create-new-partner-modal/CreateNewPartnerModal.scss";
import "./EditPartnerModal.scss";

type EditPartnerModalProps = {
  open: boolean;
  onClose: () => void;
};

const ModalHeaderSubtitle: React.FC<{ text: string }> = ({ text }) => {
  const [headlineEl, setHeadlineEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setHeadlineEl(
      document.querySelector(
        ".edit-partner-modal .hid-modal__header-headline"
      ) as HTMLElement | null
    );
  }, [text]);

  if (!headlineEl) {
    return null;
  }

  return createPortal(
    <p className="create-new-partner-modal__subtitle">{text}</p>,
    headlineEl
  );
};

const buildFormDataFromPartner = (
  partnerDetailsInitial: NonNullable<ReturnType<typeof selectPartnerDetails>>,
  parsedPhoneData: { dialCode: string; nationalNo: string }
): PartnerFormData => ({
  name: partnerDetailsInitial.partner_name || "",
  email: partnerDetailsInitial.partner_email || "",
  accountManagerEmail: partnerDetailsInitial.account_manager || "",
  type: partnerDetailsInitial.partner_type || [],
  potential: partnerDetailsInitial.partner_potential || "",
  website: partnerDetailsInitial.website || "",
  description: partnerDetailsInitial.description || "",
  region: partnerDetailsInitial.region || Constants.REGIONS[0],
  revenueStream: partnerDetailsInitial.revenue_stream || "",
  partnerVertical: (partnerDetailsInitial.partner_vertical?.split(",") || []).filter(
    (v: string) => v.trim()
  ),
  partnerTier: partnerDetailsInitial.partner_tier || "",
  country: partnerDetailsInitial.country || "",
  state: partnerDetailsInitial.state || "",
  countryCode: parsedPhoneData.dialCode,
  phoneNumber: parsedPhoneData.nationalNo,
  streetAddress: partnerDetailsInitial.street_address || "",
  registrationDate: getValidDateOrNull(partnerDetailsInitial.registration_date),
});

const buildCountrySelections = (
  partnerDetailsInitial: NonNullable<ReturnType<typeof selectPartnerDetails>>
) => {
  let selectedCountryCode: { label: string; value: string } | null = null;
  let selectedCountry: { label: string; value: string } | null = null;

  const parsedPhoneDataForDropdown = parsePhoneNumberFromString(
    partnerDetailsInitial.company_contact
  );
  if (parsedPhoneDataForDropdown) {
    const countryCodeOption = CountryCode.find(
      (country) => country.code === parsedPhoneDataForDropdown.country
    );
    if (countryCodeOption) {
      selectedCountryCode = {
        label: countryCodeOption.name,
        value: countryCodeOption.dial_code,
      };
    }
  }

  const countryOption = CountryCode.find(
    (country) => country.name === partnerDetailsInitial.country
  );
  if (countryOption) {
    selectedCountry = {
      label: countryOption.name,
      value: countryOption.name,
    };
  }

  return { selectedCountryCode, selectedCountry };
};

const EditPartnerModal: React.FC<EditPartnerModalProps> = ({ open, onClose }) => {
  const partnerDetailsInitial = useAppSelector(selectPartnerDetails);
  const isUpdateLoading = useAppSelector(selectUpdatePartnerLoading);
  const { updatePartnerStatus } = useAppSelector(
    (state) => state.partner_details.status
  );
  const dispatch = useAppDispatch();

  const parsedPhoneData = useMemo(() => {
    if (!partnerDetailsInitial?.company_contact) {
      return { dialCode: "+1", nationalNo: "" };
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(
      partnerDetailsInitial.company_contact
    );
    if (parsedPhoneNumber) {
      return {
        dialCode: "+" + parsedPhoneNumber.countryCallingCode,
        nationalNo: parsedPhoneNumber.nationalNumber,
      };
    }
    return { dialCode: "+1", nationalNo: "" };
  }, [partnerDetailsInitial?.company_contact]);

  const initialFormData = useMemo(() => {
    if (!open || !partnerDetailsInitial) {
      return null;
    }
    return buildFormDataFromPartner(partnerDetailsInitial, parsedPhoneData);
  }, [open, partnerDetailsInitial, parsedPhoneData]);

  const initialCountrySelections = useMemo(() => {
    if (!open || !partnerDetailsInitial) {
      return null;
    }
    return buildCountrySelections(partnerDetailsInitial);
  }, [open, partnerDetailsInitial]);

  const [partnerDetails, setPartnerDetails] = useState<PartnerFormData | null>(
    null
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const saveInProgressRef = useRef(false);
  const sawLoadingRef = useRef(false);

  const emailRegex = useMemo(
    () => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    []
  );

  useEffect(() => {
    if (!open) {
      setPartnerDetails(null);
      setSelectedCountryCode(null);
      setSelectedCountry(null);
      saveInProgressRef.current = false;
      sawLoadingRef.current = false;
      return;
    }

    if (initialCountrySelections) {
      setSelectedCountryCode(initialCountrySelections.selectedCountryCode);
      setSelectedCountry(initialCountrySelections.selectedCountry);
    }
  }, [open, initialCountrySelections]);

  const handleClose = useCallback(() => {
    saveInProgressRef.current = false;
    sawLoadingRef.current = false;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }
    document.body.classList.add("create-new-partner-modal-open");
    return () => {
      document.body.classList.remove("create-new-partner-modal-open");
    };
  }, [open]);

  useEffect(() => {
    if (!saveInProgressRef.current) {
      return;
    }

    if (isUpdateLoading) {
      sawLoadingRef.current = true;
      return;
    }

    if (!sawLoadingRef.current) {
      return;
    }

    saveInProgressRef.current = false;
    sawLoadingRef.current = false;
    if (updatePartnerStatus) {
      handleClose();
    }
  }, [isUpdateLoading, updatePartnerStatus, handleClose]);

  const formValues = partnerDetails ?? initialFormData;

  const isSaveButtonEnabled = useMemo(
    () =>
      Boolean(formValues?.name?.trim()) &&
      (!formValues?.accountManagerEmail ||
        emailRegex.test(formValues.accountManagerEmail)) &&
      (!formValues?.email || emailRegex.test(formValues.email)),
    [
      formValues?.name,
      formValues?.accountManagerEmail,
      formValues?.email,
      emailRegex,
    ]
  );

  const formatDateForDB = useCallback((date: Date | null) => {
    if (date) {
      return date.toLocaleDateString("en-CA");
    }
    return null;
  }, []);

  const handleSaveClick = useCallback(() => {
    if (!partnerDetailsInitial || !formValues) {
      return;
    }

    const sanitizedDescription = sanitizeHtml(formValues.description);
    dispatch(
      updatePartnerSagaAction({
        ID: partnerDetailsInitial.ID,
        partner_id: partnerDetailsInitial.partner_id,
        partner_name: formValues.name,
        description: sanitizedDescription,
        account_manager: formValues.accountManagerEmail,
        partner_type: formValues.type.join(","),
        partner_potential: formValues.potential,
        status: partnerDetailsInitial.status,
        partner_email: formValues.email,
        website: formValues.website,
        region: formValues.region,
        revenue_stream: formValues.revenueStream,
        partner_vertical: formValues.partnerVertical.join(","),
        partner_tier: formValues.partnerTier,
        country: formValues.country,
        state: formValues.state,
        company_contact: formValues.countryCode + formValues.phoneNumber,
        street_address: formValues.streetAddress,
        company_logo: partnerDetailsInitial.company_logo,
        service_tier: partnerDetailsInitial.service_tier,
        registration_date: formatDateForDB(formValues.registrationDate),
      })
    );
    saveInProgressRef.current = true;
    sawLoadingRef.current = false;
  }, [formValues, partnerDetailsInitial, formatDateForDB, dispatch]);

  const handleSetPartnerDetails = useCallback(
    (value: React.SetStateAction<PartnerFormData>) => {
      setPartnerDetails(value);
    },
    []
  );

  const resolvedCountryCode =
    selectedCountryCode ?? initialCountrySelections?.selectedCountryCode ?? null;
  const resolvedCountry =
    selectedCountry ?? initialCountrySelections?.selectedCountry ?? null;

  if (!open || !partnerDetailsInitial || !initialFormData) {
    return null;
  }

  return (
    <Modal
      optionalHeader="Partner Organizations"
      headerText="Edit Partner"
      width={920}
      className="create-new-partner-modal edit-partner-modal"
      onClose={handleClose}
    >
      <ModalHeaderSubtitle text="Update partner details" />
      <PartnerDetailForm
        key={`edit-partner-${partnerDetailsInitial.ID}`}
        layout="modal"
        partnerDetails={initialFormData}
        setPartnerDetails={handleSetPartnerDetails}
        selectedCountryCode={resolvedCountryCode}
        selectedCountry={resolvedCountry}
        setSelectedCountryCode={setSelectedCountryCode}
        setSelectedCountry={setSelectedCountry}
      />
      <div className="create-new-partner-modal__footer">
        <Button variant="secondary" label="CANCEL" onClick={handleClose} />
        <div className="edit-partner-modal__save-btn">
          {isUpdateLoading ? <Loader /> : null}
          <Button
            variant="primary"
            label="SAVE"
            disabled={!isSaveButtonEnabled || isUpdateLoading}
            onClick={handleSaveClick}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditPartnerModal;
